import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get_course_timetable } from "../../../apis/Branches";
import { C } from "../../../constants/mq";

// A course's timetable, read from the classes each branch has created.
//
// There is no timetable document to publish: a branch building its classes IS
// the timetable. Reading it back means the site can never show a stale PDF.
//
// Classes whose day and time have not been entered yet still appear, marked
// as "times to be confirmed" — hiding them would make a campus look like it
// does not run the course at all.

// Macquarie look (src/constants/mq.js): each campus is a white card with a
// sand header strip, hairline rules and charcoal text.
const s = {
  termLine: {
    fontSize: 13, color: C.muted, margin: "0 0 14px", fontWeight: 500,
  },
  termRow: {
    padding: "12px 18px 4px", fontSize: 11, fontWeight: 700,
    letterSpacing: "1.2px", textTransform: "uppercase", color: C.red,
  },
  campus: { marginBottom: 16 },
  campusHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    background: C.sand,
    borderBottom: `1px solid ${C.line}`,
    padding: "13px 18px",
  },
  campusName: { fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 },
  campusMeta: { fontSize: 12.5, color: C.muted },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: C.muted,
    padding: "10px 18px",
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 18px",
    borderBottom: `1px solid ${C.line}`,
    color: C.body,
    verticalAlign: "top",
  },
  strong: { fontWeight: 600, color: C.ink },
  tbc: { color: C.muted, fontStyle: "italic" },
  session: { textTransform: "capitalize" },
  pending: {
    padding: "22px 24px",
    fontSize: 14,
    lineHeight: 1.7,
    color: C.body,
  },
  link: { color: C.red, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 },
};

const CourseTimetable = ({ slug, year, courseName }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    get_course_timetable(slug, year)
      .then((d) => alive && setData(d))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [slug, year]);

  if (!loaded) return null;

  const campuses = data?.campuses || [];
  const showing = data?.showing || [];

  if (campuses.length === 0) {
    return (
      <div className="mq-card" style={s.pending}>
        Classes for this course have not been scheduled yet.{" "}
        <Link style={s.link} to="/contact-us">
          Contact us
        </Link>{" "}
        and we will tell you what is running at your nearest campus.
      </div>
    );
  }

  return (
    <>
      {/* Each term is timetabled again, so without naming them the same
          Saturday slot reads as two separate classes. */}
      {showing.length > 0 && (
        <p style={s.termLine}>
          Showing{" "}
          {showing.map((x, i) => (
            <span key={x.term_id}>
              {i > 0 && " and "}
              <strong style={{ color: C.ink }}>{x.label}</strong>
              {x.is_current ? " (now)" : ""}
            </span>
          ))}
        </p>
      )}
      {campuses.map((c) => (
        <div key={c.branch_id || c.branch_name} className="mq-card" style={s.campus}>
          <div style={s.campusHead}>
            <p style={s.campusName}>{c.branch_name}</p>
            <span style={s.campusMeta}>
              {[c.region, c.phone].filter(Boolean).join("  ·  ")}
            </span>
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Class</th>
                <th style={s.th}>Day</th>
                <th style={s.th}>Time</th>
                <th style={s.th}>Weeks</th>
              </tr>
            </thead>
            <tbody>
              {c.classes.map((cl, i) => (
                <React.Fragment key={cl.id}>
                  {(i === 0 || c.classes[i - 1].term_id !== cl.term_id) &&
                    showing.length > 1 && (
                      <tr>
                        <td style={s.termRow} colSpan={4}>
                          {cl.term_label}
                        </td>
                      </tr>
                    )}
                <tr>
                  <td style={{ ...s.td, ...s.strong }}>{cl.class_name}</td>
                  <td style={s.td}>
                    {cl.day || (
                      <span style={{ ...s.tbc, ...s.session }}>
                        {cl.session || "to be confirmed"}
                      </span>
                    )}
                  </td>
                  <td style={s.td}>
                    {cl.start_time ? (
                      <>
                        {cl.start_time}
                        {cl.end_time ? ` – ${cl.end_time}` : ""}
                      </>
                    ) : (
                      <span style={s.tbc}>times to be confirmed</span>
                    )}
                  </td>
                  <td style={s.td}>{cl.weeks || <span style={s.tbc}>—</span>}</td>
                </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <p style={{ fontSize: 12.5, color: C.muted, margin: "12px 0 0", lineHeight: 1.6 }}>
        Times are set by each campus and can change between terms. Call the
        campus to confirm before enrolling
        {courseName ? ` in ${courseName}` : ""}.
      </p>
    </>
  );
};

export default CourseTimetable;
